import format from "string-format";

import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import {
    BooleanMemberCondition,
    IMemberCondition,
    MemberCondition,
    NoAncestorMemberCondition,
    RelationMemberCondition,
    StringMemberCondition,
} from "../contracts/base/member-condition";
import ErrorMessages from "../error-messages";
import Guard from "../guard";
import {
    BooleanMemberConditionResource,
    MemberConditionResource,
    NoAncestorMemberConditionResource,
    RelationMemberConditionResource,
    StringMemberConditionResource,
} from "../models/member-condition-resource";

export class MemberConditionMapper {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client, "client");

        this._client = client;
    }

    //#region To SDK
    async mapMemberConditionsAsync(
        conditionResources: Array<MemberConditionResource>
    ): Promise<Array<MemberCondition>> {
        if (!conditionResources || conditionResources.length === 0) return [];

        const list: Array<MemberCondition> = [];

        for (const conditionResource of conditionResources) {
            const condition = await this.mapMemberConditionAsync(conditionResource);
            if (condition != null) {
                list.push(condition);
            }
        }

        return list;
    }

    async mapMemberConditionAsync(conditionResource: MemberConditionResource): NullableResultPromise<MemberCondition> {
        if (!conditionResource) return null;

        if (conditionResource instanceof NoAncestorMemberConditionResource) {
            const definitionId = await this._client.linkHelper.idFromEntityAsync(conditionResource.definition);

            return new NoAncestorMemberCondition(conditionResource.memberName, definitionId!);
        } else if (conditionResource instanceof RelationMemberConditionResource) {
            const valueId = await this._client.linkHelper.idFromEntityAsync(conditionResource.value);

            return new RelationMemberCondition(conditionResource.memberName, valueId!);
        } else if (conditionResource instanceof StringMemberConditionResource) {
            return new StringMemberCondition(conditionResource.memberName, conditionResource.value);
        } else if (conditionResource instanceof BooleanMemberConditionResource) {
            return new BooleanMemberCondition(conditionResource.memberName, conditionResource.value);
        } else {
            throw Error(format(ErrorMessages.UnknownMemberCondition, conditionResource.constructor.name));
        }
    }
    //#endregion

    //#region To API resource
    async mapMemberConditionResourcesAsync(
        conditions: Array<IMemberCondition>
    ): Promise<Array<MemberConditionResource>> {
        if (conditions == null || conditions.length === 0) return [];

        const list: Array<MemberCondition> = [];

        for (const condition of conditions) {
            const resource = await this.mapMemberConditionResourceAsync(condition);
            if (resource) {
                list.push(resource);
            }
        }

        return list;
    }

    async mapMemberConditionResourceAsync(condition: IMemberCondition): NullableResultPromise<MemberConditionResource> {
        if (condition == null) return null;

        let resource: MemberConditionResource;
        if (condition instanceof NoAncestorMemberCondition) {
            const definitionName = await this._client.entityDefinitions.getNameCachedAsync(condition.definitionId);
            const definitionLink = await this._client.linkHelper.definitionToLinkAsync(definitionName!);

            resource = new NoAncestorMemberConditionResource({
                memberName: condition.memberName,
                definition: definitionLink,
            });
        } else if (condition instanceof StringMemberCondition) {
            resource = new StringMemberConditionResource({
                memberName: condition.memberName,
                value: condition.value,
            });
        } else if (condition instanceof BooleanMemberCondition) {
            resource = new BooleanMemberConditionResource({
                memberName: condition.memberName,
                value: condition.value,
            });
        } else if (condition instanceof RelationMemberCondition) {
            const definitionLink = await this._client.linkHelper.entityToLinkAsync(condition.entityId);

            resource = new RelationMemberConditionResource({
                memberName: condition.memberName,
                value: definitionLink,
            });
        } else {
            throw Error(format(ErrorMessages.UnknownMemberCondition, condition.constructor.name));
        }

        return resource;
    }
    //#endregion
}
